import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ContratFormDialog from './ContratFormDialog';
import ContratMatrixManager from './ContratMatrixManager';
import ArticleCatalogManager from './ArticleCatalogManager';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const fmtHours = (value) => `${Number(value || 0).toFixed(2)} h`;
const fmtDays = (value) => `${Number(value || 0).toFixed(2)} j`;
const getHoursGap = (stats = {}) =>
  Number(stats?.totalInterventionHours || 0)
  - Number(stats?.helpdeskInterventionHours || 0)
  - Number(stats?.commandeInterventionHours || 0);

const frequencyLabel = {
  mensuel: 'Mensuel',
  annuel: 'Annuel',
};

const chartColors = ['#1f4e5f', '#4f772d', '#c97b2a', '#8d5a97', '#c44536', '#577590', '#9c6644'];
const levelKeys = ['admin', 'ingenieur', 'pilotage', 'nonAffecte'];
const levelLabels = {
  admin: 'Admin',
  ingenieur: 'Ingenieur',
  pilotage: 'Pilotage',
  nonAffecte: 'Non affecte',
};
const levelChartColors = {
  admin: '#1f4e5f',
  ingenieur: '#4f772d',
  pilotage: '#c97b2a',
  nonAffecte: '#c44536',
};
const monthLabels = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

const toIsoDateTime = (dateValue, endOfDay = false) => {
  if (!dateValue) return null;
  const suffix = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000';
  return new Date(`${dateValue}${suffix}`).toISOString();
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} EUR`;
const isInfogeranceEntry = (entry) => normalizeText(entry?.ticket?.tracker) === normalizeText('Helpdesk');
const formatHoursDelta = (value) => `${Number(value) > 0 ? '+' : ''}${Number(value || 0).toFixed(2)} h`;
const formatDaysDelta = (value) => `${Number(value) > 0 ? '+' : ''}${Number(value || 0).toFixed(2)} j`;
const formatCountDelta = (value) => `${Number(value) > 0 ? '+' : ''}${Number(value || 0).toFixed(0)}`;
const HOURS_PER_DAY = 7;

const toDays = (hours) => Number(hours || 0) / HOURS_PER_DAY;
const formatDateCell = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');

const getConfiguredDays = (contract, dayKey, legacyHourKey) => {
  const configuredDays = Number(contract?.[dayKey] || 0);
  if (configuredDays > 0) return configuredDays;
  return toDays(contract?.[legacyHourKey] || 0);
};

const getConfiguredDailyRate = (contract, dailyRateKey, legacyHourlyRateKey) => {
  const configuredDailyRate = Number(contract?.[dailyRateKey] || 0);
  if (configuredDailyRate > 0) return configuredDailyRate;
  return Number(contract?.[legacyHourlyRateKey] || 0) * HOURS_PER_DAY;
};

const getConfiguredHourlyRate = (contract, hourlyRateKey, fallbackDailyRateKey) => {
  const configuredHourlyRate = Number(contract?.[hourlyRateKey] || 0);
  if (configuredHourlyRate > 0) return configuredHourlyRate;
  return Number(contract?.[fallbackDailyRateKey] || 0) / HOURS_PER_DAY;
};

function ConsumptionDeltaCell({
  consumed,
  delta,
  contractValue,
  consumedFormatter = fmtHours,
  deltaFormatter = formatHoursDelta,
  tooltipLabel = 'Contrat',
}) {
  const numericDelta = Number(delta || 0);
  const isNegative = numericDelta < 0;
  const isPositive = numericDelta > 0;
  const chipStyles = isNegative
    ? {
        color: '#b42318',
        borderColor: '#fda29b',
        backgroundColor: '#fef3f2',
      }
    : isPositive
      ? {
          color: '#175cd3',
          borderColor: '#84caff',
          backgroundColor: '#eff8ff',
        }
      : {
          color: '#027a48',
          borderColor: '#6ce9a6',
          backgroundColor: '#ecfdf3',
        };

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2">{consumedFormatter(consumed)}</Typography>
      <Chip
        size="small"
        label={deltaFormatter(numericDelta)}
        variant="outlined"
        sx={chipStyles}
      />
      <Tooltip title={`${tooltipLabel}: ${consumedFormatter(contractValue)}`}>
        <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
      </Tooltip>
    </Stack>
  );
}

function ValueConsumptionCell({ consumedValue, contractValue }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2">{formatCurrency(consumedValue)}</Typography>
      <Tooltip title={`Contrat: ${formatCurrency(contractValue)}`}>
        <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
      </Tooltip>
    </Stack>
  );
}

function RentaValueCell({ rentaValue, contractValue }) {
  const numericValue = Number(rentaValue || 0);
  const styles = numericValue < 0
    ? { color: '#b42318', backgroundColor: '#fef3f2' }
    : numericValue > 0
      ? { color: '#175cd3', backgroundColor: '#eff8ff' }
      : { color: '#027a48', backgroundColor: '#ecfdf3' };

  return (
    <Tooltip title={`Contrat: ${formatCurrency(contractValue)}`}>
      <Chip
        size="small"
        label={formatCurrency(numericValue)}
        variant="outlined"
        sx={{
          ...styles,
          borderColor: 'currentColor',
        }}
      />
    </Tooltip>
  );
}

const getMonthRangeKeys = (startDate, endDate) => {
  if (!startDate || !endDate) return [];

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const keys = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (cursor <= last) {
    const year = cursor.getUTCFullYear();
    const month = cursor.getUTCMonth();
    keys.push({
      key: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: `${monthLabels[month]} ${year}`,
      year,
      month,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return keys;
};

const getCustomFieldValue = (entry, names = [], ids = []) => {
  const normalizedNames = names.map(normalizeText);
  const stringIds = ids.map((id) => String(id));
  const field = (entry?.customFields || []).find((item) => {
    if (ids.length > 0 && stringIds.includes(String(item?.id))) {
      return true;
    }
    return normalizedNames.includes(normalizeText(item?.name));
  });

  return typeof field?.value === 'string' ? field.value : field?.value ?? null;
};

const isModeProjetEntry = (entry) =>
  normalizeText(getCustomFieldValue(entry, ['Facturable ?', 'Facturable?'])) === normalizeText('Mode projet');

const resolveEntryMatch = (contract, skill, level) => {
  const matrixCompetences =
    Array.isArray(contract?.competences) && contract.competences.length > 0
      ? contract.competences
      : contract?.baseMatrixId?.competences || [];
  const activeCompetences = matrixCompetences.filter((item) => item?.active !== false);
  const normalizedLevel = normalizeText(level);

  if (!normalizedLevel) {
    return {
      profile: 'nonAffecte',
      matrixMatched: false,
      includedInContract: false,
      matchedLine: null,
    };
  }

  const levelMatch = activeCompetences.find(
    (item) => normalizeText(item.easyRedmineLevel) === normalizedLevel
  );

  if (!levelMatch?.profile) {
    return {
      profile: 'nonAffecte',
      matrixMatched: false,
      includedInContract: false,
      matchedLine: null,
    };
  }

  return {
    profile: levelMatch.profile,
    matrixMatched: true,
    includedInContract: levelMatch.included !== false,
    matchedLine: levelMatch,
  };
};

function EntryTable({ rows }) {
  const entryColumns = useMemo(
    () => [
      {
        accessorKey: 'spentOn',
        header: 'Date',
        Cell: ({ row }) =>
          row.original.spentOn ? new Date(row.original.spentOn).toLocaleDateString('fr-FR') : '—',
      },
      { accessorKey: 'user', header: 'Intervenant' },
      {
        accessorKey: 'hours',
        header: 'Heures',
        Cell: ({ row }) => fmtHours(row.original.hours),
      },
      {
        accessorKey: 'competence1',
        header: 'Competence 1',
        Cell: ({ row }) => row.original.competence1 || '—',
      },
      {
        accessorKey: 'competence2',
        header: 'Competence 2',
        Cell: ({ row }) => row.original.competence2 || '—',
      },
      {
        accessorKey: 'resolvedProfile',
        header: 'Niveau',
        Cell: ({ row }) => {
          const value = row.original.resolvedProfile || 'nonAffecte';
          return (
            <Chip
              size="small"
              label={levelLabels[value] || value}
              color={value === 'ingenieur' ? 'success' : value === 'pilotage' ? 'warning' : 'default'}
            />
          );
        },
      },
      {
        accessorKey: 'includedInContract',
        header: 'Contrat',
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.includedInContract ? 'Dans contrat' : 'Hors contrat'}
            color={row.original.includedInContract ? 'success' : 'warning'}
          />
        ),
      },
      {
        accessorKey: 'comments',
        header: 'Commentaire',
        Cell: ({ row }) => row.original.comments || '—',
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: entryColumns,
    data: rows,
    enablePagination: false,
    initialState: { density: 'compact' },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
}

function ContractTicketsDialog({ open, contract, tickets, entries, onClose, competenceCatalog }) {
  const competence1Id = competenceCatalog?.competence1?.fieldId;
  const competence2Id = competenceCatalog?.competence2?.fieldId;

  const normalizedEntries = useMemo(
    () =>
      (entries || []).map((entry) => {
        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);

        return {
          ...entry,
          competence1,
          competence2,
          resolvedProfile: resolvedMatch.profile,
          matrixMatched: resolvedMatch.matrixMatched,
          includedInContract: resolvedMatch.includedInContract,
        };
      }),
    [competence1Id, competence2Id, contract, entries]
  );

  const entriesByTicketId = useMemo(() => {
    const nextMap = new Map();
    normalizedEntries.forEach((entry) => {
      const key = entry.ticketId != null ? String(entry.ticketId) : '__no_ticket__';
      const existing = nextMap.get(key) || [];
      existing.push(entry);
      nextMap.set(key, existing);
    });
    return nextMap;
  }, [normalizedEntries]);

  const ticketRows = useMemo(() => {
    const ticketsById = new Map(
      (tickets || []).map((ticket) => [String(ticket.ticketId), ticket])
    );

    normalizedEntries.forEach((entry) => {
      const ticketKey = entry.ticketId != null ? String(entry.ticketId) : null;
      if (!ticketKey || ticketsById.has(ticketKey)) return;

      ticketsById.set(ticketKey, {
        ticketId: entry.ticketId,
        subject: entry?.ticket?.subject || entry.ticketSubject || 'Ticket issu du temps saisi',
        tracker: entry?.ticket?.tracker || 'Sans tracker',
        status: entry?.ticket?.status || '—',
      });
    });

    return Array.from(ticketsById.values()).map((ticket) => {
        const ticketEntries = entriesByTicketId.get(String(ticket.ticketId)) || [];
        const matchedCount = ticketEntries.filter((entry) => entry.matrixMatched).length;
        const unmatchedCount = ticketEntries.length - matchedCount;
        const outsideContractCount = ticketEntries.filter(
          (entry) => entry.matrixMatched && !entry.includedInContract
        ).length;

        return {
          ...ticket,
          ticketEntries,
          timeEntryCount: ticketEntries.length,
          totalHours: ticketEntries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0),
          matchedCount,
          unmatchedCount,
          outsideContractCount,
        };
      })
      .sort((a, b) => {
        if (b.timeEntryCount !== a.timeEntryCount) {
          return b.timeEntryCount - a.timeEntryCount;
        }
        return Number(b.ticketId || 0) - Number(a.ticketId || 0);
      });
  }, [entriesByTicketId, normalizedEntries, tickets]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'ticketId',
        header: 'Ticket',
        Cell: ({ row }) => row.original.ticketId || '—',
      },
      {
        accessorKey: 'subject',
        header: 'Sujet',
        Cell: ({ row }) => row.original.subject || '—',
      },
      {
        accessorKey: 'tracker',
        header: 'Tracker',
        Cell: ({ row }) => row.original.tracker || 'Sans tracker',
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        Cell: ({ row }) => row.original.status || '—',
      },
      {
        accessorKey: 'timeEntryCount',
        header: 'Temps saisis',
        Cell: ({ row }) => row.original.timeEntryCount || 0,
      },
      {
        accessorKey: 'totalHours',
        header: 'Heures',
        Cell: ({ row }) => fmtHours(row.original.totalHours),
      },
      {
        accessorKey: 'matchedCount',
        header: 'Dans matrice',
        Cell: ({ row }) => {
          const value = row.original.matchedCount || 0;
          return (
            <Chip size="small" label={value} color="success" />
          );
        },
      },
      {
        accessorKey: 'unmatchedCount',
        header: 'Non trouves',
        Cell: ({ row }) => {
          const value = row.original.unmatchedCount || 0;
          return (
            <Chip size="small" label={value} color={value > 0 ? 'warning' : 'default'} />
          );
        },
      },
      {
        accessorKey: 'outsideContractCount',
        header: 'Hors contrat',
        Cell: ({ row }) => {
          const value = row.original.outsideContractCount || 0;
          return <Chip size="small" label={value} color={value > 0 ? 'warning' : 'default'} />;
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: ticketRows,
    enablePagination: false,
    initialState: { density: 'compact' },
    renderDetailPanel: ({ row }) => {
      const matchedEntries = row.original.ticketEntries.filter((entry) => entry.matrixMatched);
      const unmatchedEntries = row.original.ticketEntries.filter((entry) => !entry.matrixMatched);
      const outsideContractEntries = row.original.ticketEntries.filter(
        (entry) => entry.matrixMatched && !entry.includedInContract
      );

      return (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {row.original.ticketEntries.length} temps saisi(s) pour ce ticket.
          </Typography>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Temps trouvés dans la matrice
            </Typography>
            <EntryTable rows={matchedEntries} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Temps effectués hors contrat
            </Typography>
            <EntryTable rows={outsideContractEntries} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Temps sans correspondance dans la matrice
            </Typography>
            <EntryTable rows={unmatchedEntries} />
          </Box>
        </Stack>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{`Tickets - ${contract?.name || 'Contrat'}`}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {(tickets || []).length} ticket(s). Déplie un ticket pour voir les temps saisis et leurs
          `Compétence 1` / `Compétence 2`.
        </Typography>
        <MaterialReactTable table={table} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ContratPage() {
  const currentYear = new Date().getFullYear();
  const availableYears = useMemo(
    () => Array.from({ length: 6 }, (_, index) => String(currentYear - index)),
    [currentYear]
  );

  const [tab, setTab] = useState(0);
  const [contracts, setContracts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [supportProjects, setSupportProjects] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportEntries, setSupportEntries] = useState([]);
  const [supportEntriesLoading, setSupportEntriesLoading] = useState(false);
  const [competenceCatalog, setCompetenceCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [articleOpen, setArticleOpen] = useState(false);
  const [entriesDialogOpen, setEntriesDialogOpen] = useState(false);
  const [entriesDialogContract, setEntriesDialogContract] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [dateMode, setDateMode] = useState('year');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedInfogeranceFilter, setSelectedInfogeranceFilter] = useState('');
  const [selectedRootOrganizationId, setSelectedRootOrganizationId] = useState('');
  const [selectedValueProfile, setSelectedValueProfile] = useState('all');
  const [selectedValueMonthKey, setSelectedValueMonthKey] = useState('');

  const dateRange = useMemo(() => {
    if (dateMode === 'custom') {
      return {
        startDate: toIsoDateTime(customStartDate, false),
        endDate: toIsoDateTime(customEndDate, true),
      };
    }

    return {
      startDate: `${selectedYear}-01-01T00:00:00.000Z`,
      endDate: `${selectedYear}-12-31T23:59:59.999Z`,
    };
  }, [customEndDate, customStartDate, dateMode, selectedYear]);

  const loadContracts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const { data } = await api.get('/api/contrat', { params });
      setContracts(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [dateRange.endDate, dateRange.startDate]);

  const loadOrganizations = useCallback(async () => {
    try {
      const { data } = await api.get('/api/organizations');
      const rows = Array.isArray(data) ? data : data?.data || [];
      setOrganizations(rows);
    } catch (e) {
      setOrganizations([]);
    }
  }, []);

  const loadMatrices = useCallback(async () => {
    try {
      const { data } = await api.get('/api/contrat/matrix');
      setMatrices(Array.isArray(data) ? data : []);
    } catch (e) {
      setMatrices([]);
    }
  }, []);

  const loadArticles = useCallback(async () => {
    try {
      const { data } = await api.get('/api/contrat/articles/all');
      setArticles(Array.isArray(data) ? data : []);
    } catch (e) {
      setArticles([]);
    }
  }, []);

  const loadSupportProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/api/contrat/support/projects');
      setSupportProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      setSupportProjects([]);
    }
  }, []);

  useEffect(() => {
    loadContracts();
    loadOrganizations();
    loadMatrices();
    loadArticles();
    loadSupportProjects();
  }, [loadArticles, loadContracts, loadMatrices, loadOrganizations, loadSupportProjects]);

  useEffect(() => {
    let active = true;

    const loadCompetenceCatalog = async () => {
      try {
        const { data } = await api.get('/api/easyredmine/custom-fields/competences');
        if (active) {
          setCompetenceCatalog(data?.data || null);
        }
      } catch (e) {
        if (active) {
          setCompetenceCatalog(null);
        }
      }
    };

    loadCompetenceCatalog();
    return () => {
      active = false;
    };
  }, []);

  const rootOrganizationOptions = useMemo(() => {
    const map = new Map();

    organizations.forEach((organization) => {
      const rootOrganization = organization?.rootOrganization;
      if (rootOrganization?._id) {
        map.set(String(rootOrganization._id), rootOrganization);
      }
    });

    return Array.from(map.values()).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
  }, [organizations]);

  const organizationById = useMemo(() => {
    const nextMap = new Map();
    organizations.forEach((organization) => {
      if (organization?._id) {
        nextMap.set(String(organization._id), organization);
      }
    });
    return nextMap;
  }, [organizations]);

  const clientAutocompleteOptions = useMemo(() => {
    if (!selectedRootOrganizationId) {
      return organizations;
    }

    return organizations.filter((organization) => {
      const rootOrganizationId = organization?.rootOrganization?._id;
      return String(rootOrganizationId || '') === String(selectedRootOrganizationId);
    });
  }, [organizations, selectedRootOrganizationId]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      if (selectedOrganizationId && contract.Organization?._id !== selectedOrganizationId) {
        return false;
      }

      if (selectedInfogeranceFilter === 'oui' && contract.infogerance !== true) {
        return false;
      }

      if (selectedInfogeranceFilter === 'non' && contract.infogerance !== false) {
        return false;
      }

      if (selectedRootOrganizationId) {
        const organizationDetails = organizationById.get(String(contract.Organization?._id));
        const rootOrganizationId = organizationDetails?.rootOrganization?._id;
        if (String(rootOrganizationId || '') !== String(selectedRootOrganizationId)) {
          return false;
        }
      }

      return true;
    });
  }, [contracts, organizationById, selectedInfogeranceFilter, selectedOrganizationId, selectedRootOrganizationId]);

  useEffect(() => {
    if (!selectedOrganizationId) {
      return;
    }

    const stillAvailable = clientAutocompleteOptions.some(
      (organization) => String(organization._id) === String(selectedOrganizationId)
    );

    if (!stillAvailable) {
      setSelectedOrganizationId('');
    }
  }, [clientAutocompleteOptions, selectedOrganizationId]);

  const selectedProjectIds = useMemo(() => {
    const ids = new Set();

    filteredContracts.forEach((contract) => {
      (contract.support || []).forEach((project) => {
        if (Number.isFinite(project?.projectId)) {
          ids.add(project.projectId);
        }
      });
    });

    return Array.from(ids);
  }, [filteredContracts]);

  useEffect(() => {
    let active = true;

    const loadSupportEntries = async () => {
      if (selectedProjectIds.length === 0) {
        if (active) {
          setSupportTickets([]);
          setSupportEntries([]);
          setSupportEntriesLoading(false);
        }
        return;
      }

      setSupportEntriesLoading(true);
      try {
        const params = { limit: 'all', projectIds: selectedProjectIds.join(',') };
        if (dateRange.startDate) params.from = dateRange.startDate;
        if (dateRange.endDate) params.to = dateRange.endDate;

        const [ticketsResponse, entriesResponse] = await Promise.all([
          api.get('/api/easyredmine/tickets', {
            params: {
              limit: 'all',
              projectIds: selectedProjectIds.join(','),
              updatedFrom: dateRange.startDate,
              updatedTo: dateRange.endDate,
            },
          }),
          api.get('/api/easyredmine/time-entries', { params }),
        ]);

        if (active) {
          setSupportTickets(Array.isArray(ticketsResponse?.data?.data) ? ticketsResponse.data.data : []);
          setSupportEntries(Array.isArray(entriesResponse?.data?.data) ? entriesResponse.data.data : []);
        }
      } catch (e) {
        if (active) {
          setSupportTickets([]);
          setSupportEntries([]);
        }
      } finally {
        if (active) {
          setSupportEntriesLoading(false);
        }
      }
    };

    loadSupportEntries();
    return () => {
      active = false;
    };
  }, [dateRange.endDate, dateRange.startDate, selectedProjectIds]);

  const mismatchContracts = useMemo(
    () =>
      filteredContracts.filter((contract) => {
        const total = Number(contract.supportStats?.totalInterventionHours || 0);
        const infog = Number(contract.supportStats?.helpdeskInterventionHours || 0);
        const commande = Number(contract.supportStats?.commandeInterventionHours || 0);
        return Math.abs(total - (infog + commande)) > 0.0001;
      }),
    [filteredContracts]
  );

  const supportEntriesByProjectId = useMemo(() => {
    const nextMap = new Map();

    supportEntries.forEach((entry) => {
      const key = Number(entry?.projectId);
      if (!Number.isFinite(key)) return;
      const existing = nextMap.get(key) || [];
      existing.push(entry);
      nextMap.set(key, existing);
    });

    return nextMap;
  }, [supportEntries]);

  const supportLevelStatsByContractId = useMemo(() => {
    const competence1Id = competenceCatalog?.competence1?.fieldId;
    const competence2Id = competenceCatalog?.competence2?.fieldId;
    const nextMap = new Map();

    filteredContracts.forEach((contract) => {
      const contractEntries = (contract.support || []).flatMap((project) =>
        supportEntriesByProjectId.get(project.projectId) || []
      );

      const totals = {
        totalHours: 0,
        admin: 0,
        ingenieur: 0,
        pilotage: 0,
        nonAffecte: 0,
        trackerBreakdown: new Map(),
      };

      contractEntries.forEach((entry) => {
        if (!isInfogeranceEntry(entry)) {
          return;
        }

        const hours = Number(entry?.hours || 0);
        const tracker = entry?.ticket?.tracker || 'Sans tracker';
        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const profile = resolvedMatch.profile;

        totals.totalHours += hours;
        totals[profile] += hours;

        const existingTracker = totals.trackerBreakdown.get(tracker) || {
          tracker,
          totalHours: 0,
          admin: 0,
          ingenieur: 0,
          pilotage: 0,
          nonAffecte: 0,
        };
        existingTracker.totalHours += hours;
        existingTracker[profile] += hours;
        totals.trackerBreakdown.set(tracker, existingTracker);
      });

      nextMap.set(contract._id, {
        totalHours: totals.totalHours,
        admin: totals.admin,
        ingenieur: totals.ingenieur,
        pilotage: totals.pilotage,
        nonAffecte: totals.nonAffecte,
        trackerBreakdown: Array.from(totals.trackerBreakdown.values()).sort(
          (a, b) => b.totalHours - a.totalHours
        ),
      });
    });

    return nextMap;
  }, [competenceCatalog, filteredContracts, supportEntriesByProjectId]);

  const contractEntriesById = useMemo(() => {
    const nextMap = new Map();

    filteredContracts.forEach((contract) => {
      const contractEntries = (contract.support || []).flatMap((project) =>
        supportEntriesByProjectId.get(project.projectId) || []
      );
      nextMap.set(contract._id, contractEntries);
    });

    return nextMap;
  }, [filteredContracts, supportEntriesByProjectId]);

  const supportTicketsByProjectId = useMemo(() => {
    const nextMap = new Map();

    supportTickets.forEach((ticket) => {
      const key = Number(ticket?.projectId);
      if (!Number.isFinite(key)) return;
      const existing = nextMap.get(key) || [];
      existing.push(ticket);
      nextMap.set(key, existing);
    });

    return nextMap;
  }, [supportTickets]);

  const contractTicketsById = useMemo(() => {
    const nextMap = new Map();

    filteredContracts.forEach((contract) => {
      const seen = new Map();
      (contract.support || []).forEach((project) => {
        (supportTicketsByProjectId.get(project.projectId) || []).forEach((ticket) => {
          seen.set(String(ticket.ticketId), ticket);
        });
      });
      nextMap.set(contract._id, Array.from(seen.values()));
    });

    return nextMap;
  }, [filteredContracts, supportTicketsByProjectId]);

  const periodMonths = useMemo(
    () => getMonthRangeKeys(dateRange.startDate, dateRange.endDate),
    [dateRange.endDate, dateRange.startDate]
  );

  const monthlyComparisonRows = useMemo(() => {
    return filteredContracts.flatMap((contract) =>
      periodMonths.map(({ key, label, year, month }) => {
        const adminHourlyRate = getConfiguredHourlyRate(contract, 'adminHourlyRate', 'adminDailyRate');
        const engineerHourlyRate = getConfiguredHourlyRate(contract, 'engineerHourlyRate', 'engineerDailyRate');
        const pilotageHourlyRate = getConfiguredHourlyRate(contract, 'pilotageHourlyRate', 'pilotageDailyRate');
        const adminDailyRate = getConfiguredDailyRate(contract, 'adminDailyRate', 'adminHourlyRate');
        const engineerDailyRate = getConfiguredDailyRate(contract, 'engineerDailyRate', 'engineerHourlyRate');
        const pilotageDailyRate = getConfiguredDailyRate(contract, 'pilotageDailyRate', 'pilotageHourlyRate');
        const projectConsultingAdminDailyRate = Number(contract.projectConsultingAdminDailyRate || 0);
        const projectConsultingEngineerDailyRate = Number(contract.projectConsultingEngineerDailyRate || 0);
        const row = {
          rowKey: `${contract._id}-${key}`,
          monthKey: key,
          label,
          contractId: contract._id,
          contractName: contract.name || 'Sans nom',
          client: contract.Organization?.name || 'Sans client',
          contractAdmin: Number(contract.adminHours || 0),
          consumedAdmin: 0,
          contractIngenieur: Number(contract.engineerHours || 0),
          consumedIngenieur: 0,
          contractPilotage: getConfiguredDays(contract, 'pilotageDays', 'pilotageHours'),
          consumedPilotage: 0,
          contractCadreAdminDays: Number(contract.adminDays || 0),
          consumedCadreAdminDays: 0,
          contractCadreIngenieurDays: Number(contract.engineerDays || 0),
          consumedCadreIngenieurDays: 0,
          contractProjectConsultingAdmin: Number(contract.projectConsultingAdminDays || 0),
          consumedProjectConsultingAdmin: 0,
          contractProjectConsultingEngineer: Number(contract.projectConsultingEngineerDays || 0),
          consumedProjectConsultingEngineer: 0,
          contractTravel: Number(contract.travelCount || 0),
          consumedTravel: 0,
          adminHourlyRate,
          engineerHourlyRate,
          pilotageHourlyRate,
          adminDailyRate,
          engineerDailyRate,
          pilotageDailyRate,
          projectConsultingAdminDailyRate,
          projectConsultingEngineerDailyRate,
          cadreContract: Boolean(contract.cadreContract),
        };

        const contractEntries = contractEntriesById.get(contract._id) || [];
        contractEntries.forEach((entry) => {
          if (!isInfogeranceEntry(entry)) {
            return;
          }
          if (!entry?.spentOn) return;
          const spentOn = new Date(entry.spentOn);
          if (spentOn.getUTCFullYear() !== year || spentOn.getUTCMonth() !== month) {
            return;
          }

          const competence1 = getCustomFieldValue(
            entry,
            ['Compétence', 'Competence'],
            [competenceCatalog?.competence1?.fieldId]
          );
          const competence2 = getCustomFieldValue(
            entry,
            ['Compétence2', 'Competence2'],
            [competenceCatalog?.competence2?.fieldId]
          );
          const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
          const hours = Number(entry.hours || 0);
          const days = toDays(entry.hours || 0);
          const modeProjet = isModeProjetEntry(entry);

          if (resolvedMatch.profile === 'admin') row.consumedAdmin += hours;
          if (resolvedMatch.profile === 'ingenieur') row.consumedIngenieur += hours;
          if (resolvedMatch.profile === 'pilotage') row.consumedPilotage += days;

          if (row.cadreContract) {
            if (resolvedMatch.profile === 'admin') row.consumedCadreAdminDays += days;
            if (resolvedMatch.profile === 'ingenieur') row.consumedCadreIngenieurDays += days;
          }

          if (modeProjet) {
            if (resolvedMatch.profile === 'admin') row.consumedProjectConsultingAdmin += days;
            if (resolvedMatch.profile === 'ingenieur') row.consumedProjectConsultingEngineer += days;
          }

          const hasTravel = (entry.customFields || []).some(
            (field) =>
              normalizeText(field?.name) === normalizeText('Déplacement')
              && ['1', 'true', 'oui'].includes(normalizeText(field?.value))
          );
          if (hasTravel) row.consumedTravel += 1;
        });

        row.deltaAdmin = row.contractAdmin - row.consumedAdmin;
        row.deltaIngenieur = row.contractIngenieur - row.consumedIngenieur;
        row.deltaPilotage = row.contractPilotage - row.consumedPilotage;
        row.deltaCadreAdminDays = row.contractCadreAdminDays - row.consumedCadreAdminDays;
        row.deltaCadreIngenieurDays = row.contractCadreIngenieurDays - row.consumedCadreIngenieurDays;
        row.deltaProjectConsultingAdmin = row.contractProjectConsultingAdmin - row.consumedProjectConsultingAdmin;
        row.deltaProjectConsultingEngineer =
          row.contractProjectConsultingEngineer - row.consumedProjectConsultingEngineer;
        row.deltaTravel = row.contractTravel - row.consumedTravel;
        row.contractAdminValue =
          row.contractAdmin * adminHourlyRate
          + (row.cadreContract ? row.contractCadreAdminDays * adminDailyRate : 0)
          + row.contractProjectConsultingAdmin * projectConsultingAdminDailyRate;
        row.consumedAdminValue =
          row.consumedAdmin * adminHourlyRate
          + (row.cadreContract ? row.consumedCadreAdminDays * adminDailyRate : 0)
          + row.consumedProjectConsultingAdmin * projectConsultingAdminDailyRate;
        row.contractIngenieurValue =
          row.contractIngenieur * engineerHourlyRate
          + (row.cadreContract ? row.contractCadreIngenieurDays * engineerDailyRate : 0)
          + row.contractProjectConsultingEngineer * projectConsultingEngineerDailyRate;
        row.consumedIngenieurValue =
          row.consumedIngenieur * engineerHourlyRate
          + (row.cadreContract ? row.consumedCadreIngenieurDays * engineerDailyRate : 0)
          + row.consumedProjectConsultingEngineer * projectConsultingEngineerDailyRate;
        row.contractPilotageValue = row.contractPilotage * pilotageDailyRate;
        row.consumedPilotageValue = row.consumedPilotage * pilotageDailyRate;
        row.contractTravelValue = row.contractTravel * Number(contract.travelUnitPrice || 0);
        row.consumedTravelValue = row.consumedTravel * Number(contract.travelUnitPrice || 0);
        row.contractValue =
          row.contractAdminValue
          + row.contractIngenieurValue
          + row.contractPilotageValue
          + row.contractTravelValue;
        row.consumedValue =
          row.consumedAdminValue
          + row.consumedIngenieurValue
          + row.consumedPilotageValue
          + row.consumedTravelValue;
        row.rentaValue = row.contractValue - row.consumedValue;
        row.rentaRate = row.contractValue > 0 ? (row.rentaValue / row.contractValue) * 100 : 0;
        row.isOverrun =
          row.deltaAdmin < 0
          || row.deltaIngenieur < 0
          || row.deltaPilotage < 0
          || row.deltaCadreAdminDays < 0
          || row.deltaCadreIngenieurDays < 0
          || row.deltaProjectConsultingAdmin < 0
          || row.deltaProjectConsultingEngineer < 0
          || row.deltaTravel < 0;

        return row;
      })
    )
      .sort((a, b) => {
        if (a.monthKey !== b.monthKey) {
          return a.monthKey.localeCompare(b.monthKey);
        }
        if (a.client !== b.client) {
          return a.client.localeCompare(b.client);
        }
        return a.contractName.localeCompare(b.contractName);
      });
  }, [competenceCatalog, contractEntriesById, filteredContracts, periodMonths]);

  const yearlyComparisonRows = useMemo(() => {
    const periodMonthCount = Math.max(periodMonths.length, 1);

    return filteredContracts.map((contract) => {
      const consumed = {
        admin: 0,
        ingenieur: 0,
        pilotage: 0,
        nonAffecte: 0,
        cadreAdminDays: 0,
        cadreIngenieurDays: 0,
        projectConsultingAdmin: 0,
        projectConsultingEngineer: 0,
        travelCount: 0,
      };
      const adminHourlyRate = getConfiguredHourlyRate(contract, 'adminHourlyRate', 'adminDailyRate');
      const engineerHourlyRate = getConfiguredHourlyRate(contract, 'engineerHourlyRate', 'engineerDailyRate');
      const pilotageHourlyRate = getConfiguredHourlyRate(contract, 'pilotageHourlyRate', 'pilotageDailyRate');
      const adminDailyRate = getConfiguredDailyRate(contract, 'adminDailyRate', 'adminHourlyRate');
      const engineerDailyRate = getConfiguredDailyRate(contract, 'engineerDailyRate', 'engineerHourlyRate');
      const pilotageDailyRate = getConfiguredDailyRate(contract, 'pilotageDailyRate', 'pilotageHourlyRate');
      const projectConsultingAdminDailyRate = Number(contract.projectConsultingAdminDailyRate || 0);
      const projectConsultingEngineerDailyRate = Number(contract.projectConsultingEngineerDailyRate || 0);

      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        if (!isInfogeranceEntry(entry)) {
          return;
        }
        const competence1 = getCustomFieldValue(
          entry,
          ['Compétence', 'Competence'],
          [competenceCatalog?.competence1?.fieldId]
        );
        const competence2 = getCustomFieldValue(
          entry,
          ['Compétence2', 'Competence2'],
          [competenceCatalog?.competence2?.fieldId]
        );
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const hours = Number(entry.hours || 0);
        const days = toDays(entry.hours || 0);

        consumed[resolvedMatch.profile] += hours;

        if (contract.cadreContract) {
          if (resolvedMatch.profile === 'admin') consumed.cadreAdminDays += days;
          if (resolvedMatch.profile === 'ingenieur') consumed.cadreIngenieurDays += days;
        }

        if (isModeProjetEntry(entry)) {
          if (resolvedMatch.profile === 'admin') consumed.projectConsultingAdmin += days;
          if (resolvedMatch.profile === 'ingenieur') consumed.projectConsultingEngineer += days;
        }

        const hasTravel = (entry.customFields || []).some(
          (field) =>
            normalizeText(field?.name) === normalizeText('Déplacement')
              && ['1', 'true', 'oui'].includes(normalizeText(field?.value))
        );
        if (hasTravel) consumed.travelCount += 1;
      });

      const contractAdmin = Number(contract.adminHours || 0) * periodMonthCount;
      const contractIngenieur = Number(contract.engineerHours || 0) * periodMonthCount;
      const contractPilotage = getConfiguredDays(contract, 'pilotageDays', 'pilotageHours') * periodMonthCount;
      const contractCadreAdminDays = Number(contract.adminDays || 0) * periodMonthCount;
      const contractCadreIngenieurDays = Number(contract.engineerDays || 0) * periodMonthCount;
      const contractProjectConsultingAdmin = Number(contract.projectConsultingAdminDays || 0) * periodMonthCount;
      const contractProjectConsultingEngineer = Number(contract.projectConsultingEngineerDays || 0) * periodMonthCount;

      const estimatedContractValue =
        contractAdmin * adminHourlyRate
        + contractIngenieur * engineerHourlyRate
        + contractPilotage * pilotageDailyRate
        + (contract.cadreContract ? contractCadreAdminDays * adminDailyRate : 0)
        + (contract.cadreContract ? contractCadreIngenieurDays * engineerDailyRate : 0)
        + contractProjectConsultingAdmin * projectConsultingAdminDailyRate
        + contractProjectConsultingEngineer * projectConsultingEngineerDailyRate
        + (Number(contract.travelCount || 0) * periodMonthCount * Number(contract.travelUnitPrice || 0));

      const estimatedConsumedValue =
        consumed.admin * adminHourlyRate
        + consumed.ingenieur * engineerHourlyRate
        + toDays(consumed.pilotage) * pilotageDailyRate
        + (contract.cadreContract ? consumed.cadreAdminDays * adminDailyRate : 0)
        + (contract.cadreContract ? consumed.cadreIngenieurDays * engineerDailyRate : 0)
        + consumed.projectConsultingAdmin * projectConsultingAdminDailyRate
        + consumed.projectConsultingEngineer * projectConsultingEngineerDailyRate
        + consumed.travelCount * Number(contract.travelUnitPrice || 0);

      return {
        contractId: contract._id,
        contractName: contract.name || 'Sans nom',
        client: contract.Organization?.name || 'Sans client',
        cadreContract: Boolean(contract.cadreContract),
        contractAdmin,
        consumedAdmin: consumed.admin,
        deltaAdmin: contractAdmin - consumed.admin,
        contractIngenieur,
        consumedIngenieur: consumed.ingenieur,
        deltaIngenieur: contractIngenieur - consumed.ingenieur,
        contractPilotage,
        consumedPilotage: toDays(consumed.pilotage),
        deltaPilotage: contractPilotage - toDays(consumed.pilotage),
        contractCadreAdminDays,
        consumedCadreAdminDays: consumed.cadreAdminDays,
        deltaCadreAdminDays: contractCadreAdminDays - consumed.cadreAdminDays,
        contractCadreIngenieurDays,
        consumedCadreIngenieurDays: consumed.cadreIngenieurDays,
        deltaCadreIngenieurDays: contractCadreIngenieurDays - consumed.cadreIngenieurDays,
        contractProjectConsultingAdmin,
        consumedProjectConsultingAdmin: consumed.projectConsultingAdmin,
        deltaProjectConsultingAdmin: contractProjectConsultingAdmin - consumed.projectConsultingAdmin,
        contractProjectConsultingEngineer,
        consumedProjectConsultingEngineer: consumed.projectConsultingEngineer,
        deltaProjectConsultingEngineer: contractProjectConsultingEngineer - consumed.projectConsultingEngineer,
        contractTravel: Number(contract.travelCount || 0) * periodMonthCount,
        consumedTravel: consumed.travelCount,
        deltaTravel: Number(contract.travelCount || 0) * periodMonthCount - consumed.travelCount,
        estimatedContractValue,
        estimatedConsumedValue,
        estimatedDeltaValue: estimatedContractValue - estimatedConsumedValue,
      };
    });
  }, [competenceCatalog, contractEntriesById, filteredContracts, periodMonths.length]);

  const monthlyValueChartData = useMemo(() => {
    const monthMap = new Map();

    monthlyComparisonRows.forEach((row) => {
      const existing = monthMap.get(row.monthKey) || {
        monthKey: row.monthKey,
        label: row.label,
        adminContractValue: 0,
        adminConsumedValue: 0,
        adminRentaValue: 0,
        ingenieurContractValue: 0,
        ingenieurConsumedValue: 0,
        ingenieurRentaValue: 0,
        pilotageContractValue: 0,
        pilotageConsumedValue: 0,
        pilotageRentaValue: 0,
      };

      existing.adminContractValue += Number(row.contractAdminValue || 0);
      existing.adminConsumedValue += Number(row.consumedAdminValue || 0);
      existing.ingenieurContractValue += Number(row.contractIngenieurValue || 0);
      existing.ingenieurConsumedValue += Number(row.consumedIngenieurValue || 0);
      existing.pilotageContractValue += Number(row.contractPilotageValue || 0);
      existing.pilotageConsumedValue += Number(row.consumedPilotageValue || 0);

      existing.adminRentaValue = existing.adminContractValue - existing.adminConsumedValue;
      existing.ingenieurRentaValue = existing.ingenieurContractValue - existing.ingenieurConsumedValue;
      existing.pilotageRentaValue = existing.pilotageContractValue - existing.pilotageConsumedValue;

      monthMap.set(row.monthKey, existing);
    });

    return Array.from(monthMap.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [monthlyComparisonRows]);

  const monthlyValueProfileChartData = useMemo(() => {
    return monthlyValueChartData
      .filter((item) => !selectedValueMonthKey || item.monthKey === selectedValueMonthKey)
      .map((item) => {
        if (selectedValueProfile === 'admin') {
          return {
            monthKey: item.monthKey,
            label: item.label,
            contractValue: Number(item.adminContractValue || 0),
            consumedValue: Number(item.adminConsumedValue || 0),
            rentaValue: Number(item.adminRentaValue || 0),
          };
        }

        if (selectedValueProfile === 'pilotage') {
          return {
            monthKey: item.monthKey,
            label: item.label,
            contractValue: Number(item.pilotageContractValue || 0),
            consumedValue: Number(item.pilotageConsumedValue || 0),
            rentaValue: Number(item.pilotageRentaValue || 0),
          };
        }

        if (selectedValueProfile === 'ingenieur') {
          return {
            monthKey: item.monthKey,
            label: item.label,
            contractValue: Number(item.ingenieurContractValue || 0),
            consumedValue: Number(item.ingenieurConsumedValue || 0),
            rentaValue: Number(item.ingenieurRentaValue || 0),
          };
        }

        return {
          monthKey: item.monthKey,
          label: item.label,
          adminContractValue: Number(item.adminContractValue || 0),
          adminConsumedValue: Number(item.adminConsumedValue || 0),
          ingenieurContractValue: Number(item.ingenieurContractValue || 0),
          ingenieurConsumedValue: Number(item.ingenieurConsumedValue || 0),
          pilotageContractValue: Number(item.pilotageContractValue || 0),
          pilotageConsumedValue: Number(item.pilotageConsumedValue || 0),
        };
      });
  }, [monthlyValueChartData, selectedValueMonthKey, selectedValueProfile]);

  const trackerChartData = useMemo(() => {
    const trackerMap = new Map();

    filteredContracts.forEach((contract) => {
      (supportLevelStatsByContractId.get(contract._id)?.trackerBreakdown || []).forEach((item) => {
        const key = item.tracker || 'Sans tracker';
        const existing = trackerMap.get(key) || { name: key, value: 0 };
        existing.value += Number(item.totalHours || 0);
        trackerMap.set(key, existing);
      });
    });

    return Array.from(trackerMap.values()).sort((a, b) => b.value - a.value);
  }, [filteredContracts, supportLevelStatsByContractId]);

  const clientHoursChartData = useMemo(() => {
    return filteredContracts
      .map((contract) => ({
        name: contract.Organization?.name || contract.name || 'Sans client',
        helpdesk: Number(contract.supportStats?.helpdeskInterventionHours || 0),
        commande: Number(contract.supportStats?.commandeInterventionHours || 0),
        other: Number(contract.supportStats?.otherInterventionHours || 0),
      }))
      .sort((a, b) => (b.helpdesk + b.commande + b.other) - (a.helpdesk + a.commande + a.other));
  }, [filteredContracts]);

  const trackerLevelChartData = useMemo(() => {
    const trackerMap = new Map();

    filteredContracts.forEach((contract) => {
      (supportLevelStatsByContractId.get(contract._id)?.trackerBreakdown || []).forEach((item) => {
        const existing = trackerMap.get(item.tracker) || {
          name: item.tracker,
          admin: 0,
          ingenieur: 0,
          pilotage: 0,
          nonAffecte: 0,
        };
        levelKeys.forEach((levelKey) => {
          existing[levelKey] += Number(item[levelKey] || 0);
        });
        trackerMap.set(item.tracker, existing);
      });
    });

    return Array.from(trackerMap.values()).sort(
      (a, b) =>
        (b.admin + b.ingenieur + b.pilotage + b.nonAffecte)
        - (a.admin + a.ingenieur + a.pilotage + a.nonAffecte)
    );
  }, [filteredContracts, supportLevelStatsByContractId]);

  const clientLevelChartData = useMemo(() => {
    return filteredContracts
      .map((contract) => {
        const levelStats = supportLevelStatsByContractId.get(contract._id) || {};
        return {
          name: contract.Organization?.name || contract.name || 'Sans client',
          admin: Number(levelStats.admin || 0),
          ingenieur: Number(levelStats.ingenieur || 0),
          pilotage: Number(levelStats.pilotage || 0),
          nonAffecte: Number(levelStats.nonAffecte || 0),
        };
      })
      .sort(
        (a, b) =>
          (b.admin + b.ingenieur + b.pilotage + b.nonAffecte)
          - (a.admin + a.ingenieur + a.pilotage + a.nonAffecte)
      );
  }, [filteredContracts, supportLevelStatsByContractId]);

  const userLevelChartData = useMemo(() => {
    const userMap = new Map();

    filteredContracts.forEach((contract) => {
      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        if (!isInfogeranceEntry(entry)) {
          return;
        }

        const competence1 = getCustomFieldValue(
          entry,
          ['Compétence', 'Competence'],
          [competenceCatalog?.competence1?.fieldId]
        );
        const competence2 = getCustomFieldValue(
          entry,
          ['Compétence2', 'Competence2'],
          [competenceCatalog?.competence2?.fieldId]
        );
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const userName = entry?.user || 'Sans intervenant';
        const existing = userMap.get(userName) || {
          name: userName,
          admin: 0,
          ingenieur: 0,
          pilotage: 0,
          nonAffecte: 0,
        };

        existing[resolvedMatch.profile] += Number(entry.hours || 0);
        userMap.set(userName, existing);
      });
    });

    return Array.from(userMap.values()).sort(
      (a, b) =>
        (b.admin + b.ingenieur + b.pilotage + b.nonAffecte)
        - (a.admin + a.ingenieur + a.pilotage + a.nonAffecte)
    );
  }, [competenceCatalog, contractEntriesById, filteredContracts]);

  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Contrat' },
      { accessorKey: 'reference', header: 'Reference' },
      {
        accessorKey: 'Organization.name',
        header: 'Organisation',
        Cell: ({ row }) => row.original.Organization?.name || '—',
      },
      {
        accessorKey: 'infogerance',
        header: 'Infogerance',
        Cell: ({ row }) => (
          <Chip
            size="small"
            color={row.original.infogerance ? 'success' : 'default'}
            label={row.original.infogerance ? 'Oui' : 'Non'}
          />
        ),
      },
      {
        accessorKey: 'cadreContract',
        header: 'Cadre',
        Cell: ({ row }) => (
          <Chip
            size="small"
            color={row.original.cadreContract ? 'info' : 'default'}
            label={row.original.cadreContract ? 'Oui' : 'Non'}
          />
        ),
      },
      {
        accessorKey: 'startDate',
        header: 'Date debut',
        Cell: ({ row }) => formatDateCell(row.original.startDate),
      },
      {
        accessorKey: 'endDate',
        header: 'Date fin',
        Cell: ({ row }) => formatDateCell(row.original.endDate),
      },
      {
        id: 'adminHours',
        accessorKey: 'adminHours',
        header: 'Heures admin',
        Cell: ({ row }) => fmtHours(row.original.adminHours),
      },
      {
        id: 'engineerHours',
        accessorKey: 'engineerHours',
        header: 'Heures inge',
        Cell: ({ row }) => fmtHours(row.original.engineerHours),
      },
      {
        id: 'pilotageDays',
        header: 'Jours pilotage',
        Cell: ({ row }) => fmtDays(getConfiguredDays(row.original, 'pilotageDays', 'pilotageHours')),
      },
      {
        id: 'cadreAdminDays',
        header: 'Jours admin',
        Cell: ({ row }) => fmtDays(row.original.adminDays),
      },
      {
        id: 'cadreEngineerDays',
        header: 'Jours inge',
        Cell: ({ row }) => fmtDays(row.original.engineerDays),
      },
      {
        accessorKey: 'adminHourlyRate',
        header: 'Prix h admin',
        Cell: ({ row }) => formatCurrency(getConfiguredHourlyRate(row.original, 'adminHourlyRate', 'adminDailyRate')),
      },
      {
        accessorKey: 'engineerHourlyRate',
        header: 'Prix h inge',
        Cell: ({ row }) => formatCurrency(getConfiguredHourlyRate(row.original, 'engineerHourlyRate', 'engineerDailyRate')),
      },
      {
        accessorKey: 'projectConsultingAdminDays',
        header: 'Jours projet admin',
        Cell: ({ row }) => fmtDays(row.original.projectConsultingAdminDays),
      },
      {
        accessorKey: 'projectConsultingEngineerDays',
        header: 'Jours projet inge',
        Cell: ({ row }) => fmtDays(row.original.projectConsultingEngineerDays),
      },
      {
        id: 'adminDailyRate',
        header: 'Prix jour admin',
        Cell: ({ row }) => formatCurrency(row.original.adminDailyRate),
      },
      {
        id: 'engineerDailyRate',
        header: 'Prix jour inge',
        Cell: ({ row }) => formatCurrency(row.original.engineerDailyRate),
      },
      {
        id: 'pilotageDailyRate',
        header: 'Prix jour pilotage',
        Cell: ({ row }) => formatCurrency(getConfiguredDailyRate(row.original, 'pilotageDailyRate', 'pilotageHourlyRate')),
      },
      {
        accessorKey: 'projectConsultingAdminDailyRate',
        header: 'Prix jour projet admin',
        Cell: ({ row }) => formatCurrency(row.original.projectConsultingAdminDailyRate),
      },
      {
        accessorKey: 'projectConsultingEngineerDailyRate',
        header: 'Prix jour projet inge',
        Cell: ({ row }) => formatCurrency(row.original.projectConsultingEngineerDailyRate),
      },
      {
        accessorKey: 'travelUnitPrice',
        header: 'Prix deplacement',
        Cell: ({ row }) => formatCurrency(row.original.travelUnitPrice),
      },
      {
        accessorKey: 'travelCount',
        header: 'Deplacements',
        Cell: ({ row }) => row.original.travelCount ?? 0,
      },
      {
        accessorKey: 'billingFrequency',
        header: 'Frequence',
        Cell: ({ row }) => frequencyLabel[row.original.billingFrequency] || '—',
      },
      {
        accessorKey: 'baseMatrixId.name',
        header: 'Matrice',
        Cell: ({ row }) => row.original.baseMatrixId?.name || '—',
      },
      {
        id: 'support',
        header: 'Support EasyRedmine',
        Cell: ({ row }) =>
          row.original.support?.length
            ? row.original.support.map((item) => item.projectName).join(', ')
            : '—',
      },
      {
        id: 'articles',
        header: 'Articles',
        Cell: ({ row }) => row.original.articles?.length || 0,
      },
      {
        id: 'totalInterventionHours',
        header: 'Heures intervention',
        Cell: ({ row }) => {
          const gap = getHoursGap(row.original.supportStats);
          const hasGap = Math.abs(gap) > 0.0001;

          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                {fmtHours(row.original.supportStats?.totalInterventionHours)}
              </Typography>
              {hasGap ? (
                <Tooltip
                  title={`Ecart detecte: ${gap > 0 ? '+' : ''}${gap.toFixed(2)} h par rapport a InfoG + hors InfoG`}
                >
                  <WarningAmberIcon fontSize="small" color="warning" />
                </Tooltip>
              ) : null}
            </Stack>
          );
        },
      },
      {
        id: 'helpdeskInterventionHours',
        header: 'Heures InfoG',
        Cell: ({ row }) => fmtHours(row.original.supportStats?.helpdeskInterventionHours),
      },
      {
        id: 'commandeInterventionHours',
        header: 'Heures hors InfoG',
        Cell: ({ row }) => fmtHours(row.original.supportStats?.commandeInterventionHours),
      },
      {
        id: 'ticketCount',
        header: 'Tickets',
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setEntriesDialogContract(row.original);
              setEntriesDialogOpen(true);
            }}
          >
            {row.original.supportStats?.ticketCount ?? 0}
          </Button>
        ),
      },
      {
        id: 'supportAdminHours',
        header: 'Support admin',
        Cell: ({ row }) => fmtHours(supportLevelStatsByContractId.get(row.original._id)?.admin),
      },
      {
        id: 'supportEngineerHours',
        header: 'Support ingenieur',
        Cell: ({ row }) => fmtHours(supportLevelStatsByContractId.get(row.original._id)?.ingenieur),
      },
      {
        id: 'supportPilotageHours',
        header: 'Support pilotage',
        Cell: ({ row }) => fmtHours(supportLevelStatsByContractId.get(row.original._id)?.pilotage),
      },
      {
        id: 'supportUnassignedHours',
        header: 'Support non affecte',
        Cell: ({ row }) => fmtHours(supportLevelStatsByContractId.get(row.original._id)?.nonAffecte),
      },
      {
        id: 'supportHelpdeskTravelCount',
        header: 'Dep. InfoG',
        Cell: ({ row }) => row.original.supportStats?.helpdeskTravelCount ?? 0,
      },
      {
        id: 'supportCommandeTravelCount',
        header: 'Dep. commande',
        Cell: ({ row }) => row.original.supportStats?.commandeTravelCount ?? 0,
      },
      {
        id: 'supportTravelCount',
        header: 'Dep. reels total',
        Cell: ({ row }) => row.original.supportStats?.travelCount ?? 0,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(row.original);
                  setFormOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                color="error"
                onClick={async () => {
                  if (!window.confirm(`Supprimer le contrat "${row.original.name}" ?`)) return;
                  await api.delete(`/api/contrat/${row.original._id}`);
                  loadContracts();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [loadContracts, supportLevelStatsByContractId]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredContracts,
    state: { isLoading: loading },
    initialState: { density: 'compact' },
    enablePagination: false,
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  const monthlyComparisonTable = useMaterialReactTable({
    columns: [
      { accessorKey: 'contractName', header: 'Contrat' },
      { accessorKey: 'client', header: 'Client' },
      { accessorKey: 'label', header: 'Mois' },
      {
        accessorKey: 'isOverrun',
        header: 'Statut',
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.cadreContract ? 'Cadre' : row.original.isOverrun ? 'Depassement' : 'OK'}
            color={row.original.cadreContract ? 'info' : row.original.isOverrun ? 'warning' : 'success'}
          />
        ),
      },
      {
        id: 'adminSummary',
        header: 'Admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedAdmin}
            delta={row.original.deltaAdmin}
            contractValue={row.original.contractAdmin}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'ingenieurSummary',
        header: 'Ingenieur',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedIngenieur}
            delta={row.original.deltaIngenieur}
            contractValue={row.original.contractIngenieur}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'pilotageSummary',
        header: 'Pilotage',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedPilotage}
            delta={row.original.deltaPilotage}
            contractValue={row.original.contractPilotage}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'cadreAdminSummary',
        header: 'Cadre admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreAdminDays}
            delta={row.original.deltaCadreAdminDays}
            contractValue={row.original.contractCadreAdminDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'cadreIngenieurSummary',
        header: 'Cadre inge',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreIngenieurDays}
            delta={row.original.deltaCadreIngenieurDays}
            contractValue={row.original.contractCadreIngenieurDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'projectConsultingAdminSummary',
        header: 'Projet admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedProjectConsultingAdmin}
            delta={row.original.deltaProjectConsultingAdmin}
            contractValue={row.original.contractProjectConsultingAdmin}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'projectConsultingEngineerSummary',
        header: 'Projet inge',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedProjectConsultingEngineer}
            delta={row.original.deltaProjectConsultingEngineer}
            contractValue={row.original.contractProjectConsultingEngineer}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'travelSummary',
        header: 'Deplacements',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedTravel}
            delta={row.original.deltaTravel}
            contractValue={row.original.contractTravel}
            consumedFormatter={(value) => `${Number(value || 0)}`}
            deltaFormatter={formatCountDelta}
          />
        ),
      },
      {
        id: 'valueConsumedSummary',
        header: 'Valeur consommee',
        Cell: ({ row }) => (
          <ValueConsumptionCell
            consumedValue={row.original.consumedValue}
            contractValue={row.original.contractValue}
          />
        ),
      },
      {
        id: 'valueRentaSummary',
        header: 'Renta',
        Cell: ({ row }) => (
          <RentaValueCell
            rentaValue={row.original.rentaValue}
            contractValue={row.original.contractValue}
          />
        ),
      },
      {
        accessorKey: 'rentaRate',
        header: 'Renta %',
        Cell: ({ row }) => `${Number(row.original.rentaRate || 0).toFixed(1)} %`,
      },
    ],
    data: monthlyComparisonRows,
    enablePagination: false,
    initialState: { density: 'compact' },
  });

  const yearlyComparisonTable = useMaterialReactTable({
    columns: [
      { accessorKey: 'contractName', header: 'Contrat' },
      { accessorKey: 'client', header: 'Client' },
      {
        id: 'yearAdminSummary',
        header: 'Admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedAdmin}
            delta={row.original.deltaAdmin}
            contractValue={row.original.contractAdmin}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'yearIngenieurSummary',
        header: 'Ingenieur',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedIngenieur}
            delta={row.original.deltaIngenieur}
            contractValue={row.original.contractIngenieur}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'yearPilotageSummary',
        header: 'Pilotage',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedPilotage}
            delta={row.original.deltaPilotage}
            contractValue={row.original.contractPilotage}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'yearCadreAdminSummary',
        header: 'Cadre admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreAdminDays}
            delta={row.original.deltaCadreAdminDays}
            contractValue={row.original.contractCadreAdminDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'yearCadreIngenieurSummary',
        header: 'Cadre inge',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreIngenieurDays}
            delta={row.original.deltaCadreIngenieurDays}
            contractValue={row.original.contractCadreIngenieurDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'yearProjectConsultingAdminSummary',
        header: 'Projet admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedProjectConsultingAdmin}
            delta={row.original.deltaProjectConsultingAdmin}
            contractValue={row.original.contractProjectConsultingAdmin}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'yearProjectConsultingEngineerSummary',
        header: 'Projet inge',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedProjectConsultingEngineer}
            delta={row.original.deltaProjectConsultingEngineer}
            contractValue={row.original.contractProjectConsultingEngineer}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'yearTravelSummary',
        header: 'Deplacements',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedTravel}
            delta={row.original.deltaTravel}
            contractValue={row.original.contractTravel}
            consumedFormatter={(value) => `${Number(value || 0)}`}
            deltaFormatter={formatCountDelta}
          />
        ),
      },
      { accessorKey: 'estimatedContractValue', header: 'Valeur contrat', Cell: ({ row }) => formatCurrency(row.original.estimatedContractValue) },
      { accessorKey: 'estimatedConsumedValue', header: 'Valeur consommee', Cell: ({ row }) => formatCurrency(row.original.estimatedConsumedValue) },
      { accessorKey: 'estimatedDeltaValue', header: 'Valeur delta', Cell: ({ row }) => formatCurrency(row.original.estimatedDeltaValue) },
    ],
    data: yearlyComparisonRows,
    enablePagination: false,
    initialState: { density: 'compact' },
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6">Contrats</Typography>
          <Typography variant="caption" color="text.secondary">
            Gestion des contrats, matrices de competences et catalogue d&apos;articles
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setMatrixOpen(true)}>
            Matrices
          </Button>
          <Button variant="outlined" startIcon={<Inventory2Icon />} onClick={() => setArticleOpen(true)}>
            Articles
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            Nouveau contrat
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            select
            label="Mode de periode"
            value={dateMode}
            onChange={(e) => setDateMode(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="year">Par annee</MenuItem>
            <MenuItem value="custom">Personnalise</MenuItem>
          </TextField>

          {dateMode === 'year' ? (
            <TextField
              select
              label="Annee"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <>
              <TextField
                label="Date debut"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Date fin"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <Autocomplete
            options={[
              { _id: '', name: 'Toutes les racines' },
              ...rootOrganizationOptions,
            ]}
            value={
              [{ _id: '', name: 'Toutes les racines' }, ...rootOrganizationOptions].find(
                (organization) => organization._id === selectedRootOrganizationId
              ) || null
            }
            onChange={(_, value) => setSelectedRootOrganizationId(value?._id || '')}
            getOptionLabel={(option) => option?.name || 'Sans nom'}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={{ minWidth: 240 }}
            renderInput={(params) => <TextField {...params} label="Root organization" />}
          />

          <Autocomplete
            options={[
              { _id: '', name: 'Tous les clients' },
              ...clientAutocompleteOptions,
            ]}
            value={
              [{ _id: '', name: 'Tous les clients' }, ...clientAutocompleteOptions].find(
                (organization) => organization._id === selectedOrganizationId
              ) || null
            }
            onChange={(_, value) => setSelectedOrganizationId(value?._id || '')}
            getOptionLabel={(option) => option?.name || 'Sans nom'}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={{ minWidth: 280 }}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />

          <TextField
            select
            label="Infogerance"
            value={selectedInfogeranceFilter}
            onChange={(e) => setSelectedInfogeranceFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Tous</MenuItem>
            <MenuItem value="oui">Oui</MenuItem>
            <MenuItem value="non">Non</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {mismatchContracts.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {mismatchContracts.length} contrat(s) ont un ecart entre `heures intervention` et `heures InfoG + heures hors
          InfoG`. Cela indique des trackers autres que `Helpdesk` ou `Commande`:{" "}
          {mismatchContracts.map((contract) => contract.name).join(', ')}
        </Alert>
      ) : null}

      {!competenceCatalog ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Le mapping des competences EasyRedmine n&apos;est pas disponible. La repartition par niveau restera vide ou non
          affectee tant que `/easyredmine/custom-fields/competences` n&apos;est pas alimente.
        </Alert>
      ) : null}

      {supportEntriesLoading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Chargement des time entries EasyRedmine pour calculer la repartition par niveau.
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} mb={2}>
        <Paper sx={{ p: 2, flex: 1, minHeight: 360 }}>
          <Typography variant="subtitle1" mb={2}>
            Repartition globale des heures par tracker
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={trackerChartData} dataKey="value" nameKey="name" outerRadius={110} label>
                  {trackerChartData.map((entry, index) => (
                    <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                  ))}
                </Pie>
                <Legend />
                <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)} h`} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minHeight: 360 }}>
          <Typography variant="subtitle1" mb={2}>
            Repartition des heures par client
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={clientHoursChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide />
                <YAxis />
                <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)} h`} />
                <Legend />
                <Bar dataKey="helpdesk" stackId="hours" fill="#1f4e5f" name="Helpdesk / InfoG" />
                <Bar dataKey="commande" stackId="hours" fill="#4f772d" name="Commande / Hors InfoG" />
                <Bar dataKey="other" stackId="hours" fill="#c97b2a" name="Autres trackers" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Stack>

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} mb={2}>
        <Paper sx={{ p: 2, flex: 1, minHeight: 360 }}>
          <Typography variant="subtitle1" mb={2}>
            Repartition des heures par tracker et niveau
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trackerLevelChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)} h`} />
                <Legend />
                {levelKeys.map((levelKey) => (
                  <Bar
                    key={levelKey}
                    dataKey={levelKey}
                    stackId="level"
                    fill={levelChartColors[levelKey]}
                    name={levelLabels[levelKey]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minHeight: 360 }}>
          <Typography variant="subtitle1" mb={2}>
            Repartition des heures par client et niveau
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={clientLevelChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide />
                <YAxis />
                <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)} h`} />
                <Legend />
                {levelKeys.map((levelKey) => (
                  <Bar
                    key={levelKey}
                    dataKey={levelKey}
                    stackId="level"
                    fill={levelChartColors[levelKey]}
                    name={levelLabels[levelKey]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Stack>

      <Paper sx={{ p: 2, mb: 2, minHeight: 360 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          mb={2}
        >
          <Typography variant="subtitle1">
            Valeur mensuelle contrat vs consommee par profil
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Profil"
              value={selectedValueProfile}
              onChange={(e) => setSelectedValueProfile(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="ingenieur">Ingenieur</MenuItem>
              <MenuItem value="pilotage">Pilotage</MenuItem>
            </TextField>
            <TextField
              select
              label="Mois"
              value={selectedValueMonthKey}
              onChange={(e) => setSelectedValueMonthKey(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Tous les mois</MenuItem>
              {monthlyValueChartData.map((item) => (
                <MenuItem key={item.monthKey} value={item.monthKey}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyValueProfileChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <RechartsTooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              {selectedValueProfile === 'all' ? (
                <>
                  <Bar dataKey="adminContractValue" fill="#90caf9" name="Admin contrat" />
                  <Bar dataKey="adminConsumedValue" fill="#1f4e5f" name="Admin consomme" />
                  <Bar dataKey="ingenieurContractValue" fill="#a5d6a7" name="Inge contrat" />
                  <Bar dataKey="ingenieurConsumedValue" fill="#4f772d" name="Inge consomme" />
                  <Bar dataKey="pilotageContractValue" fill="#ffcc80" name="Pilotage contrat" />
                  <Bar dataKey="pilotageConsumedValue" fill="#c97b2a" name="Pilotage consomme" />
                </>
              ) : (
                <>
                  <Bar dataKey="contractValue" fill="#175cd3" name="Contrat" />
                  <Bar dataKey="consumedValue" fill="#b42318" name="Consomme" />
                  <Line type="monotone" dataKey="rentaValue" stroke="#027a48" name="Renta" strokeWidth={2} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2, minHeight: 360 }}>
        <Typography variant="subtitle1" mb={2}>
          Repartition des niveaux par intervenant
        </Typography>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={userLevelChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)} h`} />
              <Legend />
              {levelKeys.map((levelKey) => (
                <Bar
                  key={levelKey}
                  dataKey={levelKey}
                  stackId="level"
                  fill={levelChartColors[levelKey]}
                  name={levelLabels[levelKey]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={2}>
          Comparaison mensuelle contrat vs consomme
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La consommation est filtrée par la période sélectionnée, et comparée mois par mois aux heures contractuelles.
        </Typography>
        <MaterialReactTable table={monthlyComparisonTable} />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={2}>
          Comparaison annuelle / periode par contrat
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Synthèse par contrat avec heures, déplacements et valorisation à partir des tarifs configurés.
        </Typography>
        <MaterialReactTable table={yearlyComparisonTable} />
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, next) => setTab(next)}>
          <Tab label={`Contrats (${filteredContracts.length})`} />
          <Tab label={`Matrices (${matrices.length})`} />
          <Tab label={`Articles (${articles.length})`} />
        </Tabs>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {tab === 0 ? <MaterialReactTable table={table} /> : null}
      {tab === 1 ? (
        <ContratMatrixManager
          inline
          api={api}
          onChanged={() => {
            loadMatrices();
            loadContracts();
          }}
        />
      ) : null}
      {tab === 2 ? (
        <ArticleCatalogManager
          inline
          api={api}
          onChanged={() => {
            loadArticles();
            loadContracts();
          }}
        />
      ) : null}

      <ContratFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          loadContracts();
          loadMatrices();
          loadArticles();
        }}
        editing={editing}
        organizations={organizations}
        matrices={matrices}
        articles={articles}
        supportProjects={supportProjects}
        api={api}
      />

      <ContratMatrixManager
        open={matrixOpen}
        onClose={() => setMatrixOpen(false)}
        api={api}
        onChanged={() => {
          loadMatrices();
          loadContracts();
        }}
      />

      <ArticleCatalogManager
        open={articleOpen}
        onClose={() => setArticleOpen(false)}
        api={api}
        onChanged={() => {
          loadArticles();
          loadContracts();
        }}
      />

      <ContractTicketsDialog
        open={entriesDialogOpen}
        contract={entriesDialogContract}
        tickets={entriesDialogContract ? contractTicketsById.get(entriesDialogContract._id) || [] : []}
        entries={entriesDialogContract ? contractEntriesById.get(entriesDialogContract._id) || [] : []}
        competenceCatalog={competenceCatalog}
        onClose={() => {
          setEntriesDialogOpen(false);
          setEntriesDialogContract(null);
        }}
      />
    </Box>
  );
}
